/**
 * Centralized theme and color scheme management
 * Controls both CSS and Three.js material colors
 */

export type ThemeMode = 'light' | 'dark';

interface ColorPalette {
  background: string;
  bgGradientEnd: string;
  foreground: string;
  primary: string;
  secondary: string;
  accent: string;
  text: string;
  textSecondary: string;
  border: string;
  detailBg: string;
  objectDefault: number; // Three.js uses hex numbers
  objectHover: number;
  objectActive: number;
}

const lightTheme: ColorPalette = {
  background: '#d4d0cc',
  bgGradientEnd: '#b8a080',
  foreground: '#585450',
  primary: '#006080',
  secondary: '#00a0c0',
  accent: '#c88040',
  text: '#181410',
  textSecondary: '#383430',
  border: '#585450',
  detailBg: 'rgba(255, 255, 255, 0.3)',
  objectDefault: 0x006080,
  objectHover: 0x00a0c0,
  objectActive: 0x00a0c0,
};

const darkTheme: ColorPalette = {
  background: '#004060',
  bgGradientEnd: '#001020',
  foreground: '#1e293b',
  primary: '#b89460',
  secondary: '#b8a080',
  accent: '#00a0c0',
  text: '#f8ffff',
  textSecondary: '#d4d0cc',
  border: '#d4d0cc',
  detailBg: 'rgba(0, 0, 0, 0.3)',
  objectDefault: 0x60a5fa,   // light blue
  objectHover: 0xa78bfa,     // light purple
  objectActive: 0xf472b6,    // light pink
};

export class ThemeManager {
  private static instance: ThemeManager;
  private currentTheme: ThemeMode = 'light';
  private colorPalette: ColorPalette = darkTheme;
  private listeners: Set<() => void> = new Set();

  private constructor() {
    this.loadThemeFromStorage();
    this.applyTheme();
  }

  static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }

  private loadThemeFromStorage(): void {
    const stored = localStorage.getItem('theme-mode');
    if (stored === 'light' || stored === 'dark') {
      this.currentTheme = stored;
    } else {
      // Default to dark, or use system preference
      this.currentTheme = 'light';
    }
  }

  private saveThemeToStorage(): void {
    localStorage.setItem('theme-mode', this.currentTheme);
  }

  private updateColorPalette(): void {
    this.colorPalette = this.currentTheme === 'light' ? lightTheme : darkTheme;
  }

  private applyTheme(): void {
    this.updateColorPalette();

    document.documentElement.setAttribute('data-theme', this.currentTheme);
    document.body.setAttribute('data-theme', this.currentTheme);

    // Push palette values into CSS custom properties so main.css
    // doesn't need to duplicate them per-theme.
    this.applyCssProperties();

    // Notify listeners (Three.js scene etc.)
    this.notifyListeners();
  }

  private applyCssProperties(): void {
    const root = document.documentElement;
    const body = document.body;
    const p = this.colorPalette;
    // Set variables on :root so html/background chrome areas stay in sync,
    // then mirror to body for compatibility with existing inheritance.
    root.style.setProperty('--bg-primary', p.background);
    root.style.setProperty('--bg-secondary', p.foreground);
    root.style.setProperty('--bg-gradient-end', p.bgGradientEnd);
    root.style.setProperty('--color-primary', p.primary);
    root.style.setProperty('--color-secondary', p.secondary);
    root.style.setProperty('--color-accent', p.accent);
    root.style.setProperty('--text-primary', p.text);
    root.style.setProperty('--text-secondary', p.textSecondary);
    root.style.setProperty('--border-color', p.border);
    root.style.setProperty('--detail-bg', p.detailBg);

    if (body) {
      body.style.setProperty('--bg-primary', p.background);
      body.style.setProperty('--bg-secondary', p.foreground);
      body.style.setProperty('--bg-gradient-end', p.bgGradientEnd);
      body.style.setProperty('--color-primary', p.primary);
      body.style.setProperty('--color-secondary', p.secondary);
      body.style.setProperty('--color-accent', p.accent);
      body.style.setProperty('--text-primary', p.text);
      body.style.setProperty('--text-secondary', p.textSecondary);
      body.style.setProperty('--border-color', p.border);
      body.style.setProperty('--detail-bg', p.detailBg);
    }

    // iOS Safari paints the safe-area strip around the notch/dynamic
    // island (and the status bar area when the address bar is hidden)
    // using the browser's own chrome color, not the page's own
    // background — it reads that color from <meta name="theme-color">
    // rather than any element's CSS background. Keep it in sync with
    // the current theme's background so that area doesn't show through
    // as a mismatched/unstyled strip.
    const themeColorMeta = document.getElementById('theme-color-meta');
    themeColorMeta?.setAttribute('content', p.background);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  toggleTheme(): void {
    this.currentTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.saveThemeToStorage();
    this.applyTheme();
  }

  setTheme(theme: ThemeMode): void {
    if (this.currentTheme !== theme) {
      this.currentTheme = theme;
      this.saveThemeToStorage();
      this.applyTheme();
    }
  }

  getTheme(): ThemeMode {
    return this.currentTheme;
  }

  getColors(): ColorPalette {
    return { ...this.colorPalette };
  }

  getColor(colorKey: keyof Omit<ColorPalette, 'objectDefault' | 'objectHover' | 'objectActive'>): string {
    return this.colorPalette[colorKey];
  }

  getThreeJsColor(state: 'default' | 'hover' | 'active'): number {
    switch (state) {
      case 'hover':
        return this.colorPalette.objectHover;
      case 'active':
        return this.colorPalette.objectActive;
      default:
        return this.colorPalette.objectDefault;
    }
  }

  getBackgroundColor(): number {
    // Convert hex string to Three.js hex number
    return parseInt(this.colorPalette.background.slice(1), 16);
  }

  getAccentColor(): number {
    // Convert hex string to Three.js hex number
    return parseInt(this.colorPalette.accent.slice(1), 16);
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
}

export const themeManager = ThemeManager.getInstance();
